import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { fetchCompanyContests, fetchCategories, fetchSubcategories } from '../services/api';
import ParticipantsModal from './ParticipantsModal';
import { toast } from 'react-toastify';

const CompanyContestsModal = ({ isOpen, onClose, company }) => {
  const [contests, setContests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 0,
    size: 10,
    totalPages: 0,
    totalElements: 0
  });
  const [isParticipantsModalOpen, setIsParticipantsModalOpen] = useState(false);
  const [selectedContest, setSelectedContest] = useState(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  
  const [filters, setFilters] = useState({
    heading: '',
    companyName: '',
    category: '',
    subcategory: '',
    submissionStartDate: '',
    submissionEndDate: '',
    isActive: '',
    ein: '',
    email: '',
    sort: 'id,desc'
  });
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [isLoadingFilters, setIsLoadingFilters] = useState(false);

  useEffect(() => {
    if (isOpen && company) {
      fetchContestsData();
    }
  }, [isOpen, company, pagination.page, filters]);

  // Fetch categories on component mount
  useEffect(() => {
    if (isOpen) {
      const loadCategories = async () => {
        setIsLoadingFilters(true);
        try {
          const data = await fetchCategories();
          setCategories(data);
          
          // If we have a category filter already applied, find its ID
          if (filters.category) {
            const categoryObj = data.find(cat => cat.name === filters.category);
            if (categoryObj) {
              setSelectedCategoryId(categoryObj.id.toString());
              // Load the subcategories for this category
              await loadSubcategoriesForCategory(categoryObj.id);
            }
          }
        } catch (error) {
          toast.error('Failed to fetch categories');
        } finally {
          setIsLoadingFilters(false);
        }
      };

      loadCategories();
    }
  }, [isOpen, filters.category]);

  // Function to load subcategories for a specific category ID
  const loadSubcategoriesForCategory = async (categoryId) => {
    if (!categoryId) {
      setSubcategories([]);
      return;
    }

    setIsLoadingFilters(true);
    try {
      const data = await fetchSubcategories(categoryId);
      setSubcategories(data || []);
    } catch (error) {
      toast.error('Failed to fetch subcategories');
      setSubcategories([]);
    } finally {
      setIsLoadingFilters(false);
    }
  };

  const fetchContestsData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = {
        page: pagination.page,
        size: pagination.size,
        sort: filters.sort,
        companyName: company.name, // Filter by selected company name
        ein: company.ein // Also filter by company EIN
      };
      
      // Add optional filters if they exist
      if (filters.heading) params.heading = filters.heading;
      if (filters.category) params.category = filters.category;
      if (filters.subcategory) params.subcategory = filters.subcategory;
      if (filters.submissionStartDate) params.submissionStartDate = filters.submissionStartDate;
      if (filters.submissionEndDate) params.submissionEndDate = filters.submissionEndDate;
      if (filters.isActive !== '') params.isActive = filters.isActive === 'true';
      if (filters.email) params.email = filters.email;
      
      const response = await fetchCompanyContests(params);
      
      setContests(response.content || []);
      setPagination(prev => ({
        ...prev,
        totalPages: response.totalPages || 0,
        totalElements: response.totalElements || 0
      }));
    } catch (err) {
      setError('Failed to fetch contests. Please try again.');
      console.error('Error fetching company contests:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setPagination(prev => ({
      ...prev,
      page: 0
    }));
  };

  const handleCategoryChange = (e) => {
    const categoryId = e.target.value;
    setSelectedCategoryId(categoryId);
    
    if (categoryId) {
      // Find the category name from the selected ID
      const categoryObj = categories.find(cat => cat.id.toString() === categoryId);
      if (categoryObj) {
        handleFilterChange('category', categoryObj.name);
        // Load subcategories for this category
        loadSubcategoriesForCategory(categoryId);
      } else {
        handleFilterChange('category', '');
      }
    } else {
      handleFilterChange('category', '');
      setSubcategories([]);
    }
    
    // Reset subcategory when category changes
    handleFilterChange('subcategory', '');
  };

  const handleSubcategoryChange = (e) => {
    const subcategoryId = e.target.value;
    
    if (subcategoryId) {
      // Find the subcategory name from the selected ID
      const subcategoryObj = subcategories.find(subcat => subcat.id.toString() === subcategoryId);
      if (subcategoryObj) {
        handleFilterChange('subcategory', subcategoryObj.name);
      } else {
        handleFilterChange('subcategory', '');
      }
    } else {
      handleFilterChange('subcategory', '');
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({
      ...prev,
      page: newPage
    }));
  };

  const clearFilters = () => {
    setFilters({
      heading: '',
      companyName: '',
      category: '',
      subcategory: '',
      submissionStartDate: '',
      submissionEndDate: '',
      isActive: '',
      ein: '',
      email: '',
      sort: 'id,desc'
    });
    setSelectedCategoryId('');
    setSubcategories([]);
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
    } catch (error) {
      return dateString;
    }
  };

  const handleParticipantsClick = (contest) => {
    setSelectedContest(contest);
    setIsParticipantsModalOpen(true);
  };

  const closeParticipantsModal = () => {
    setIsParticipantsModalOpen(false);
    setSelectedContest(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-7xl w-full max-h-[95vh] overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h3 className="text-xl font-semibold text-gray-900">
            Contests for {company?.name}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Filters */}
        <div className="border-b bg-gray-50">
          {/* Mobile Filter Toggle Button */}
          <div className="md:hidden border-b border-gray-200">
            <button
              type="button"
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className="w-full p-4 text-left flex justify-between items-center hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
            >
              <span className="text-sm font-medium text-gray-900">Filters</span>
              <svg
                className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${isFilterOpen ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>

          {/* Filter Form */}
          <div className={`${isFilterOpen ? 'block' : 'hidden'} md:block p-6`}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contest Heading
              </label>
              <input
                type="text"
                value={filters.heading}
                onChange={(e) => handleFilterChange('heading', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Search by heading..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                value={selectedCategoryId}
                onChange={handleCategoryChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={isLoadingFilters}
              >
                <option value="">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Subcategory
              </label>
              <select
                value={subcategories.find(s => s.name === filters.subcategory)?.id || ''}
                onChange={handleSubcategoryChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={!selectedCategoryId || isLoadingFilters}
              >
                <option value="">All Subcategories</option>
                {subcategories.map((subcat) => (
                  <option key={subcat.id} value={subcat.id}>
                    {subcat.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={filters.isActive}
                onChange={(e) => handleFilterChange('isActive', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date From
              </label>
              <input
                type="datetime-local"
                value={filters.submissionStartDate}
                onChange={(e) => handleFilterChange('submissionStartDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date To
              </label>
              <input
                type="datetime-local"
                value={filters.submissionEndDate}
                onChange={(e) => handleFilterChange('submissionEndDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={filters.email}
                onChange={(e) => handleFilterChange('email', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Search by email..."
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={clearFilters}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Clear Filters
              </button>
            </div>
          </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto" style={{ maxHeight: 'calc(95vh - 280px)' }}>
          {error && (
            <div className="p-6 bg-red-100 border border-red-400 text-red-700">
              <p>{error}</p>
            </div>
          )}

          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          ) : contests.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              <p>No contests found for this company.</p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contest Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Category / Subcategory
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Dates
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Prizes
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Participants
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {contests.map((contest) => (
                      <tr key={contest.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{contest.heading}</div>
                          <div className="text-sm text-gray-500 truncate max-w-xs" title={contest.requirement}>
                            {contest.requirement}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{contest.category}</div>
                          <div className="text-sm text-gray-500">{contest.subcategory}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">Start: {formatDate(contest.submissionStartDate)}</div>
                          <div className="text-sm text-gray-500">End: {formatDate(contest.submissionEndDate)}</div>
                          <div className="text-sm text-gray-500">Result: {formatDate(contest.resultDate)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            contest.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {contest.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {contest.prizes?.map((prize) => (
                              <div key={prize.id || prize.position}>
                                {prize.position}. ${prize.amount}
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {contest.participantCount > 0 ? (
                            <button
                              onClick={() => handleParticipantsClick(contest)}
                              className="text-sm text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                            >
                              {contest.participantCount}
                            </button>
                          ) : (
                            <div className="text-sm text-gray-900">0</div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden">
                {contests.map((contest) => (
                  <div key={contest.id} className="bg-white border-b border-gray-200 p-4">
                    <div className="flex justify-between items-start mb-3">
                      <h3 className="text-lg font-medium text-gray-900">{contest.heading}</h3>
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        contest.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {contest.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mb-3 line-clamp-2">{contest.requirement}</p>
                    
                    <div className="space-y-2 text-sm">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <span className="font-medium text-gray-700">Category:</span>
                          <p className="text-gray-900">{contest.category}</p>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Subcategory:</span>
                          <p className="text-gray-900">{contest.subcategory}</p>
                        </div>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Start Date:</span>
                        <p className="text-gray-900">{formatDate(contest.submissionStartDate)}</p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">End Date:</span>
                        <p className="text-gray-900">{formatDate(contest.submissionEndDate)}</p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Result Date:</span>
                        <p className="text-gray-900">{formatDate(contest.resultDate)}</p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Prizes:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {contest.prizes?.map((prize) => (
                            <span key={prize.id || prize.position} className="bg-gray-100 px-2 py-1 rounded text-xs">
                              {prize.position}. ${prize.amount}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Participants:</span>
                        {contest.participantCount > 0 ? (
                          <button
                            onClick={() => handleParticipantsClick(contest)}
                            className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer ml-1"
                          >
                            {contest.participantCount}
                          </button>
                        ) : (
                          <p className="text-gray-900">0</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Pagination */}
        {!loading && contests.length > 0 && (
          <div className="p-6 border-t bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {pagination.page * pagination.size + 1} to {Math.min((pagination.page + 1) * pagination.size, pagination.totalElements)} of {pagination.totalElements} contests
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 0}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-700">
                  Page {pagination.page + 1} of {pagination.totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.totalPages - 1}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Participants Modal */}
        <ParticipantsModal
          isOpen={isParticipantsModalOpen}
          onClose={closeParticipantsModal}
          contest={selectedContest}
        />
      </div>
    </div>
  );
};

export default CompanyContestsModal; 